import {
  category,
  debt,
  isNull,
  project,
  tx,
  user_exchange_rate,
  wallet,
} from "@cashsync/powersync";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SOFT-DELETE — CENTRALIZED LIVE LAYER (single choke point)              │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * This module is the ONLY place where the invariant "live row = `deleted_at IS
 * NULL`" is expressed for CashSync's 6 trash-enabled tables. Every live read
 * composes these predicates; the ONLY reads that see deleted rows are
 * those of the trash (`getTrashed*Query`, explicit whitelist in
 * `guard-manifest.ts`) and harmless id-scoped lookups (detail by id /
 * read-before-write).
 *
 * Why centralize? The scattered invariant was the #1 bug family of the
 * campaign (e.g. `checkUnsyncedActor` counted `tx`/`wallet` in trash → spurious
 * "you have local data" prompt). With a single choke point + anti-regression
 * guard, adding a raw read of these tables outside the whitelist
 * BREAKS the build (see `live/__tests__/live-access-guard.test.ts`).
 *
 * ─── No cohorts (CashSync design decision, "verify reality") ─────────────
 * Unlike the POS (comandero), where a line of a CANCELLED order must
 * disappear from reports (`NOT EXISTS` parent-in-trash cohort), in
 * finance each soft-delete entity can be INDEPENDENTLY trashed/restored
 * and NO parent exists whose deletion should retroactively hide its children from
 * historical reports:
 *   - Sending a `wallet` to the trash removes it from the accounts list, but its
 *     transactions remain real, attributable, and live movements until
 *     the wallet is PURGED (the physical purge replicates `purgeWalletMutation`:
 *     wallet→tx cascade). Hiding them retroactively would falsify reports of
 *     past periods.
 *   - Deleting a `category`/`project` leaves `category_id`/`project_id` dangling
 *     (the tx remains live, it only loses the label) — consistent with
 *     `purgeCategoryMutation` (txs keep the dangling id) and with the category
 *     tree promoting orphans to root.
 * Existing soft-delete mutations DELIBERATELY do not cascade, so
 * all tables are ROOTS (only their own `deleted_at`). Sweep CC-34
 * confirmed that no live aggregation sums rows IN TRASH (all filter
 * `deleted_at`); the only real leak was `checkUnsyncedActor` (fixed).
 */

export function liveWalletFilters() {
  return [isNull(wallet.deleted_at)];
}

export function liveCategoryFilters() {
  return [isNull(category.deleted_at)];
}

export function liveTxFilters() {
  return [isNull(tx.deleted_at)];
}

export function liveProjectFilters() {
  return [isNull(project.deleted_at)];
}

export function liveDebtFilters() {
  return [isNull(debt.deleted_at)];
}

export function liveUserExchangeRateFilters() {
  return [isNull(user_exchange_rate.deleted_at)];
}
